import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories, featuredCollections, testimonials } from '@/mocks/comics';

const heroBgImage = 'https://readdy.ai/api/search-image?query=A%20cinematic%20wide%20anime%20style%20illustration%20depicting%20a%20grand%20manga%20library%20with%20floating%20manga%20pages%20and%20cherry%20blossom%20petals%2C%20warm%20golden%20sunset%20light%20streaming%20through%20windows%2C%20Japanese%20and%20Chinese%20comic%20books%20on%20shelves%2C%20dreamy%20atmosphere%2C%20soft%20gradient%20sky%2C%20beautiful%20pastel%20toned%20color%20palette%2C%20highly%20detailed%20digital%20art%2C%20no%20text%2C%20peaceful%20and%20inviting%20mood&width=1920&height=900&seq=hero-bg-manga-01&orientation=landscape';

const statBadgeBg = 'https://readdy.ai/api/search-image?query=Soft%20abstract%20watercolor%20background%20with%20warm%20peach%20and%20cream%20tones%2C%20delicate%20paper%20texture%2C%20subtle%20gradient%2C%20minimalist%20Japanese%20aesthetic%2C%20light%20and%20airy%20feel%2C%20no%20objects%20or%20text&width=800&height=400&seq=stats-bg-manga&orientation=landscape';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroComics = comics.slice(0, 5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroComics.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroComics.length]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src={heroBgImage}
            alt="古古漫画网 - 国漫日漫韩漫大全"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <h1 className="font-heading text-2xl md:text-5xl text-background-50 mb-3 md:mb-4">
              古古漫画网
            </h1>
            <p className="text-sm md:text-lg text-background-200 max-w-2xl leading-relaxed">
              国漫日漫韩漫大全 · 免费全集下拉式阅读 · 最新漫画每日更新 · 追更连载不错过
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
              <Link
                to="/category/guoman"
                className="px-6 py-2.5 md:px-8 md:py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-all text-sm md:text-base whitespace-nowrap"
              >
                开始阅读
              </Link>
              <Link
                to="/category/hanman"
                className="px-6 py-2.5 md:px-8 md:py-3 bg-background-50/20 hover:bg-background-50/30 backdrop-blur-sm text-background-50 border border-background-50/30 font-medium rounded-full transition-all text-sm md:text-base whitespace-nowrap"
              >
                热门韩漫
              </Link>
            </div>
          </div>

          {/* Hero Slider Dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {heroComics.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-2 h-2 rounded-full transition-all whitespace-nowrap ${
                  idx === currentSlide ? 'bg-primary-500 w-6' : 'bg-background-50/50'
                }`}
                aria-label={`切换到第 ${idx + 1} 张`}
              />
            ))}
          </div>
        </section>

        {/* Stats Bar */}
        <section className="relative bg-secondary-900">
          <img
            src={statBadgeBg}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-10"
            aria-hidden="true"
          />
          <div className="relative px-4 md:px-6 py-6 md:py-8">
            <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { value: '50,000+', label: '漫画资源' },
                { value: '1,200万+', label: '月活用户' },
                { value: '每日更新', label: '最新连载' },
                { value: '99.7%', label: '用户好评' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="font-heading text-xl md:text-2xl text-primary-400">{stat.value}</p>
                  <p className="text-xs md:text-sm text-background-300 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
                漫画分类
              </h2>
              <Link to="/category/guoman" className="text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all cursor-pointer group"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                    <i className={`${cat.icon} text-lg md:text-xl`}></i>
                  </div>
                  <div>
                    <p className="font-heading text-sm md:text-base text-foreground-900 whitespace-nowrap">{cat.name}</p>
                    <p className="text-[11px] md:text-xs text-foreground-500 mt-0.5 line-clamp-1">{cat.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
                🔥 热门推荐
              </h2>
              <Link to="/category/guoman" className="text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap">
                更多推荐 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
              {comics.slice(0, 6).map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900">
                📰 最新更新
              </h2>
              <Link to="/category/guoman" className="text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap">
                全部更新 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
              {comics.slice(3, 9).map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Featured Collection */}
        {featuredCollections.slice(0, 1).map((collection) => {
          const collectionComics = collection.comicIds
            .map((id) => comics.find((c) => c.id === id))
            .filter(Boolean) as typeof comics;

          return (
            <section key={collection.id} className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-900">{collection.title}</h2>
                  <p className="text-sm text-foreground-500 mt-2">{collection.description}</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                  {collectionComics.map((comic) => (
                    <ComicCard key={comic.id} {...comic} />
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        {/* Testimonials */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8">用户好评</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/70 text-left">
                  <div className="flex items-center gap-3 mb-3">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover" />
                    <div>
                      <p className="font-medium text-sm text-foreground-900">{t.name}</p>
                      <div className="flex gap-0.5 mt-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 py-12 md:py-16 bg-secondary-900 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl text-background-50 mb-3">
              还等什么？马上开始你的漫画之旅！
            </h2>
            <p className="text-sm text-background-300 mb-6">
              海量国漫、日漫、韩漫，免费全集下拉式阅读，每日更新，追更不等待
            </p>
            <Link
              to="/category/guoman"
              className="inline-flex px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-all text-base whitespace-nowrap"
            >
              立即探索
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}